import { useCallback, useEffect, useState } from "react";

import type { StageKey } from "./StagesSidebar";

// ── Types ──────────────────────────────────────────────────────────────────────

export interface PipelineNavState {
  run: string | null;
  stage: StageKey;
  job: string | null;
  env: string | null;
}

export interface PipelineNavActions {
  setRun: (runId: string | null) => void;
  setStage: (stage: StageKey) => void;
  setJob: (jobName: string | null) => void;
  setEnv: (envSlug: string | null) => void;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const PARAM_RUN = "run";
const PARAM_STAGE = "stage";
const PARAM_JOB = "job";
const PARAM_ENV = "env";

const DEFAULT_STAGE: StageKey = "summary";

// ── Helpers ────────────────────────────────────────────────────────────────────

function readParams(): PipelineNavState {
  const sp = new URLSearchParams(window.location.search);
  return {
    run: sp.get(PARAM_RUN) ?? null,
    stage: (sp.get(PARAM_STAGE) as StageKey) || DEFAULT_STAGE,
    job: sp.get(PARAM_JOB) ?? null,
    env: sp.get(PARAM_ENV) ?? null,
  };
}

function writeParams(updates: Record<string, string | null>, push = false) {
  const sp = new URLSearchParams(window.location.search);
  for (const [key, value] of Object.entries(updates)) {
    if (value === null) sp.delete(key);
    else sp.set(key, value);
  }
  const url =
    window.location.pathname + (sp.toString() ? "?" + sp.toString() : "");
  if (push) {
    window.history.pushState(null, "", url);
  } else {
    window.history.replaceState(null, "", url);
  }
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * URL-driven pipeline navigation state.
 *
 * Reads `run`, `stage`, `job`, `env` from URL query params and writes
 * them back via `history.replaceState` on user interaction. Navigation
 * survives page reload and browser back/forward.
 *
 * Bypasses react-router's `useSearchParams` to avoid stale-URL bugs when
 * multiple params change in the same tick. Uses `popstate` listener to
 * detect browser back/forward.
 */
export function usePipelineSearchParams(): PipelineNavState &
  PipelineNavActions {
  const [localState, setLocalState] = useState<PipelineNavState>(readParams);

  // Listen for browser back/forward (popstate)
  useEffect(() => {
    const handler = () => setLocalState(readParams());
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  // Helper: update both local state and URL atomically
  const updateParams = useCallback(
    (updates: Record<string, string | null>, push = false) => {
      setLocalState((prev) => {
        const next = { ...prev };
        if (PARAM_RUN in updates) next.run = updates[PARAM_RUN] ?? null;
        if (PARAM_STAGE in updates)
          next.stage = (updates[PARAM_STAGE] as StageKey) || DEFAULT_STAGE;
        if (PARAM_JOB in updates) next.job = updates[PARAM_JOB] ?? null;
        if (PARAM_ENV in updates) next.env = updates[PARAM_ENV] ?? null;
        return next;
      });
      writeParams(updates, push);
    },
    [],
  );

  // ── Setters ────────────────────────────────────────────────────────────────

  // Run selection creates a new history entry (major navigation)
  const setRun = useCallback(
    (runId: string | null) => {
      const updates: Record<string, string | null> = {};
      if (runId) {
        updates[PARAM_RUN] = runId;
        // Read current URL to check if this is a run switch
        const currentRun = new URLSearchParams(window.location.search).get(
          PARAM_RUN,
        );
        if (runId !== currentRun) {
          updates[PARAM_STAGE] = null;
          updates[PARAM_JOB] = null;
        }
      } else {
        updates[PARAM_RUN] = null;
        updates[PARAM_STAGE] = null;
        updates[PARAM_JOB] = null;
      }
      updateParams(updates, true); // push = new history entry
    },
    [updateParams],
  );

  // Stage change creates a new history entry (major navigation)
  const setStage = useCallback(
    (s: StageKey) => {
      const updates: Record<string, string | null> = {};
      if (s === DEFAULT_STAGE) {
        updates[PARAM_STAGE] = null;
      } else {
        updates[PARAM_STAGE] = s;
      }
      // Changing stage clears job selection
      updates[PARAM_JOB] = null;
      updateParams(updates, true); // push = new history entry
    },
    [updateParams],
  );

  // Job switch replaces the current entry (minor navigation)
  const setJob = useCallback(
    (jobName: string | null) => {
      updateParams({ [PARAM_JOB]: jobName }); // replace = no new history entry
    },
    [updateParams],
  );

  // Env switch replaces the current entry (minor navigation)
  const setEnv = useCallback(
    (envSlug: string | null) => {
      updateParams({ [PARAM_ENV]: envSlug }); // replace = no new history entry
    },
    [updateParams],
  );

  return { ...localState, setRun, setStage, setJob, setEnv };
}
